import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Eyebrow from "@/components/Eyebrow";
import SEO from "@/components/SEO";
import FavoriteButton from "@/components/FavoriteButton";
import { PROGRAMS, PROGRAM_COLORS, PROGRAM_LABELS } from "@/data/programs";

const FILTERS = [
  { key: "all", label: "All programs" },
  { key: "education", label: "Education" },
  { key: "research", label: "Research" },
  { key: "social", label: "Social" },
  { key: "economic", label: "Economic" },
  { key: "health", label: "Health" },
];

export default function Programs() {
  const [filter, setFilter] = useState("all");
  const filtered = useMemo(
    () => (filter === "all" ? PROGRAMS : PROGRAMS.filter((p) => p.key === filter)),
    [filter]
  );

  return (
    <section className="pt-24 pb-16">
      <SEO title="Programs" description="15+ HOFA programs across the ERSEH framework — Education, Research, Social Engagement, Economic Empowerment, and Health Services." path="/programs" />
      <div className="max-w-[1140px] mx-auto px-6">
        <Eyebrow>Programs</Eyebrow>
        <h1 className="text-[clamp(38px,5vw,60px)] mb-4 max-w-[22ch] font-display font-bold">
          15+ programs, one woven fabric.
        </h1>
        <p className="text-ink/75 max-w-[62ch] mb-10">
          Filter by ERSEH pillar to explore the programs HOFA runs today. Each is community-led and rooted in Mango Town, Brewerville.
        </p>

        <div className="flex flex-wrap gap-2 mb-10" role="tablist">
          {FILTERS.map((f) => {
            const active = filter === f.key;
            return (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                data-testid={`programs-filter-${f.key}`}
                className={`px-5 py-2 rounded-full text-sm font-bold border-2 transition ${
                  active ? "bg-palm text-white border-palm" : "bg-white text-ink border-ink/15 hover:border-palm"
                }`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p) => (
            <div key={p.slug} className="relative">
              <Link
                to={`/programs/${p.slug}`}
                data-testid={`program-card-${p.slug}`}
                className="bg-white border border-ink/10 rounded-[14px] overflow-hidden hover:-translate-y-1 hover:shadow-xl transition no-underline text-ink flex flex-col h-full"
              >
                <div className="aspect-[16/9] overflow-hidden bg-ink/5">
                  <img src={p.image} alt="" loading="lazy" className="w-full h-full object-cover" />
                </div>
                <div className="p-6 flex flex-col gap-2.5 flex-1">
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[11.5px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 rounded-full text-white"
                      style={{ background: PROGRAM_COLORS[p.key] }}
                    >
                      {PROGRAM_LABELS[p.key]}
                    </span>
                    {p.isNew && (
                      <span className="text-[11.5px] font-bold tracking-[0.1em] uppercase px-2.5 py-1 rounded-full bg-mango text-palm-deep">New</span>
                    )}
                  </div>
                  <h3 className="text-lg font-display">{p.title}</h3>
                  {p.full && <p className="text-sm font-semibold text-ink/60">{p.full}</p>}
                  <p className="text-[15px] text-ink/75 flex-1">{p.tagline}</p>
                  <span className="text-palm font-bold text-sm mt-2">Read more →</span>
                </div>
              </Link>
              <FavoriteButton slug={p.slug} className="absolute top-3 right-3 z-10" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
